import type {CopilotChatReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {sendEvent} from '@github-ui/hydro-analytics'
import {CheckSpinner} from '@github-ui/mergebox/check-spinner'
import {testIdProps} from '@github-ui/test-id-props'
import {
  CheckIcon,
  CommentIcon,
  DotFillIcon,
  FoldIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  IssueClosedIcon,
  IssueDraftIcon,
  IssueOpenedIcon,
  PersonIcon,
  SkipIcon,
  StopIcon,
  UnfoldIcon,
} from '@primer/octicons-react'
import {AvatarStack, Label, RelativeTime, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import {load} from 'js-yaml'
import {useEffect, useId, useMemo, useRef, useState} from 'react'

import {useContentPreview} from '../../../components/ContentPreview/ContentPreviewContext'
import {lookupTaskState, useTaskStateSync} from '../../../hooks/useTaskStateSync'
import {useContentPreviewBlockContext} from '../../ContentPreviewBlockContext'
import styles from './IssueListBlock.module.css'

interface ListItem {
  type?: 'issue' | 'pr' | 'task'
  url?: string
  state?: string
  draft?: boolean
  created_at?: string
  closed_at?: string
  merged_at?: string
  title?: string
  number?: number
  labels?: string[]
  author?: string
  comments?: number
  assignees_avatar_urls?: string[]
  repository?: string
}

const ListItem = {
  /** Being extremely defensive about the types here allows us to safely handle partial data and bad model results. */
  fromUnknown: (data: unknown): ListItem => {
    if (typeof data !== 'object' || data === null) return {}

    // There's probably a library that could do this less tediously
    const item: ListItem = {}

    if ('url' in data && typeof data.url === 'string') item.url = data.url
    if ('state' in data && typeof data.state === 'string') item.state = data.state
    if ('draft' in data && typeof data.draft === 'boolean') item.draft = data.draft
    // eslint-disable-next-line camelcase
    if ('created_at' in data && typeof data.created_at === 'string') item.created_at = data.created_at
    // eslint-disable-next-line camelcase
    if ('closed_at' in data && typeof data.closed_at === 'string') item.closed_at = data.closed_at
    // eslint-disable-next-line camelcase
    if ('merged_at' in data && typeof data.merged_at === 'string') item.merged_at = data.merged_at
    if ('title' in data && typeof data.title === 'string') item.title = data.title
    if ('number' in data && typeof data.number === 'number') item.number = data.number
    if ('labels' in data && Array.isArray(data.labels)) item.labels = data.labels.filter(el => typeof el === 'string')
    if ('author' in data && typeof data.author === 'string') item.author = data.author
    if ('comments' in data && typeof data.comments === 'number') item.comments = data.comments
    if ('assignees_avatar_urls' in data && Array.isArray(data.assignees_avatar_urls))
      // eslint-disable-next-line camelcase
      item.assignees_avatar_urls = data.assignees_avatar_urls.filter(el => typeof el === 'string')
    if ('repository' in data && typeof data.repository === 'string') item.repository = data.repository

    return item
  },
}

function parseGitHubIssueUrl(url: string) {
  const pattern = /^https?:\/\/[^/]+\/([^/]+)\/([^/]+)\/issues\/(\d+)(\/.*)?$/
  const match = pattern.exec(url)
  if (!match) {
    return {}
  }
  const number = parseInt(match[3] ?? '0', 10)
  return {owner: match[1], repo: match[2], number}
}

function getIconProps(item: ListItem) {
  let icon = null
  let colorClass: string | undefined
  if (item.type === 'issue') {
    if (item.state === 'open' && !item.draft) {
      icon = <IssueOpenedIcon aria-label="Open issue" />
      colorClass = styles.open
    } else if (item.state === 'closed' && !item.draft) {
      icon = <IssueClosedIcon aria-label="Closed issue" />
      colorClass = styles.done
    } else if (item.state === 'open' && item.draft) {
      icon = <IssueDraftIcon aria-label="Draft issue" />
      colorClass = styles.draft
    } else if (item.state === 'closed' && item.draft) {
      icon = <SkipIcon aria-label="Closed draft issue" />
      colorClass = styles.draft
    }
  } else if (item.type === 'pr') {
    const isMerged = typeof item.merged_at === 'string' && item.merged_at.length > 0
    if (item.state === 'open' && !isMerged && item.draft) {
      icon = <GitPullRequestDraftIcon aria-label="Draft pull request" />
      colorClass = styles.draft
    } else if (item.state === 'open' && !isMerged && !item.draft) {
      icon = <GitPullRequestIcon aria-label="Pull request" />
      colorClass = styles.open
    } else if (item.state === 'closed' && !isMerged) {
      icon = <GitPullRequestClosedIcon aria-label="Closed pull request" />
      colorClass = styles.closed
    } else if (item.state === 'closed' && isMerged) {
      icon = <GitMergeIcon aria-label="Merged pull request" />
      colorClass = styles.done
    }
  } else if (item.type === 'task') {
    if (item.state === 'queued') {
      icon = <DotFillIcon aria-label="Task queued" className={styles.dotFillIcon} />
      colorClass = styles.open
    } else if (item.state === 'pending' || item.state === 'idle') {
      icon = <DotFillIcon aria-label="Task pending" className={styles.dotFillIcon} />
      colorClass = styles.open
    } else if (item.state === 'running' || item.state === 'in_progress') {
      icon = (
        <span role="img" aria-label="Task running">
          <CheckSpinner animated />
        </span>
      )
      colorClass = styles.open
    } else if (item.state === 'completed') {
      icon = <CheckIcon aria-label="Task completed" className={styles.checkIcon} />
      colorClass = styles.done
    } else if (item.state === 'waiting_for_user') {
      icon = <DotFillIcon aria-label="Task waiting for user input" className={styles.dotFillIcon} />
      colorClass = styles.open
    } else if (item.state === 'failed' || item.state === 'timed_out') {
      icon = <StopIcon aria-label="Task failed" className={styles.stopIcon} />
      colorClass = styles.closed
    } else if (item.state === 'cancelled') {
      icon = <StopIcon aria-label="Task cancelled" className={styles.stopIcon} />
      colorClass = styles.closed
    } else {
      icon = <DotFillIcon aria-label="Task inactive" className={styles.dotFillIcon} />
      colorClass = styles.open
    }
  }

  return {icon, colorClass}
}

function Description({item, isStreaming}: {item: ListItem; isStreaming: boolean}) {
  const author = item.author
  let dateValue = undefined
  let action = undefined

  if (item.merged_at) {
    dateValue = item.merged_at
    action = 'Merged'
  } else if (item.closed_at) {
    dateValue = item.closed_at
    action = 'Closed'
  } else if (item.created_at) {
    dateValue = item.created_at
    action = 'Opened'
  }

  let parsedDate = dateValue ? new Date(dateValue) : null
  if (parsedDate && isNaN(parsedDate.getTime())) parsedDate = null

  const hasRepository = !!item.repository

  // Original logic: show nothing while streaming if we don't have (author AND date)
  // But now we also want to show if we have repository even while streaming
  if ((!author || !parsedDate) && isStreaming && !hasRepository) {
    return null
  }

  return (
    <span className={styles.description}>
      {hasRepository && (
        <>
          {item.repository}
          {(author || parsedDate) && ' · '}
        </>
      )}
      {(author || parsedDate) && action && (
        <>
          {author} {author ? action.toLowerCase() : action}{' '}
          {parsedDate && (
            <>
              <RelativeTime date={parsedDate} format="relative" />
            </>
          )}
        </>
      )}
    </span>
  )
}

const parseListData = (yaml: string) => {
  try {
    const result = load(yaml)
    if (typeof result === 'object' && result !== null && 'data' in result && Array.isArray(result.data))
      return result.data.map(ListItem.fromUnknown)
    return null
  } catch {
    return null
  }
}

const refTypeFor = (type: 'issue' | 'pr' | 'task'): 'github.issue-list' | 'github.pr-list' | 'github.task-list' => {
  switch (type) {
    case 'issue':
      return 'github.issue-list'
    case 'pr':
      return 'github.pr-list'
    case 'task':
      return 'github.task-list'
  }
}

/**
 * Look up a structured list reference on the message by id and return its
 * items. Returns undefined when no matching reference is found, in which case
 * the caller falls back to YAML parsing.
 */
const findStructuredListItems = (
  references: CopilotChatReference[],
  id: string,
  type: 'issue' | 'pr' | 'task',
): ListItem[] | undefined => {
  const expectedType = refTypeFor(type)
  const ref = references.find(r => r.type === expectedType && 'id' in r && r.id === id)
  if (!ref || !('items' in ref)) return undefined
  return ref.items.map(ListItem.fromUnknown)
}

export interface ListBlockProps {
  /** List item data as YAML string. Used as fallback when no matching structured reference is found. */
  data: string
  type: 'issue' | 'pr' | 'task'
  isStreaming: boolean
  /**
   * Optional id from the fence meta (`id=...`). When present and the matching
   * structured list reference is found on the message, items are sourced from
   * the structured reference instead of YAML body parsing.
   */
  id?: string
}

export function ListBlock({data, type, isStreaming: isBlockStreaming, id: refId}: ListBlockProps) {
  const {messageId, references} = useContentPreviewBlockContext()
  const structuredItems = useMemo(() => {
    if (!refId || !references) return undefined
    return findStructuredListItems(references, refId, type)
  }, [refId, references, type])

  const [yamlItems, setYamlItems] = useState<ListItem[]>(() => parseListData(data) ?? [])
  const items = structuredItems ?? yamlItems
  const {updateItem, openItem, openPreviewPane} = useContentPreview()
  const [collapsed, setCollapsed] = useState(true)
  const hasMoreItemsEnabled = isFeatureEnabled('copilot_issue_list_show_more')
  const taskStateMap = useTaskStateSync({enabled: type === 'task'})
  const firstNewItemRef = useRef<HTMLLIElement>(null)
  const justExpandedRef = useRef(false)
  const hydrationReportedRef = useRef(false)
  const listId = useId()

  // Default to showing 10 items when collapsed
  const initialVisibleCount = 10
  const visibleItems = hasMoreItemsEnabled && collapsed ? items.slice(0, initialVisibleCount) : items
  const hasMoreItems = hasMoreItemsEnabled && items.length > initialVisibleCount

  useEffect(() => {
    if (justExpandedRef.current && !collapsed && firstNewItemRef.current) {
      const focusTarget = firstNewItemRef.current.querySelector<HTMLElement>('a, button')
      focusTarget?.focus()
      justExpandedRef.current = false
    }
  }, [collapsed])

  // Ignore intermediate updates with bad data (partially streamed YAML)
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setYamlItems(lastParsedData => parseListData(data) ?? lastParsedData), [data])

  useEffect(() => {
    if (type === 'issue') {
      sendEvent('dotcom_chat.activate', {target: 'ISSUE_LIST_BLOCK_RENDERED', mode: 'immersive'})
    } else if (type === 'pr') {
      sendEvent('dotcom_chat.activate', {target: 'PR_LIST_BLOCK_RENDERED', mode: 'immersive'})
    } else if (type === 'task') {
      sendEvent('dotcom_chat.activate', {target: 'TASK_LIST_BLOCK_RENDERED', mode: 'immersive'})
    }
  }, [type])

  // Report list placeholder hydration once per block so we can measure how often
  // a model-emitted placeholder (`id=...`) is backed by a structured reference
  // (hydrated) versus falling back to YAML parsing. Reported after streaming
  // completes, by which point any structured references have arrived.
  useEffect(() => {
    if (!refId || isBlockStreaming || hydrationReportedRef.current) return
    hydrationReportedRef.current = true
    sendEvent('dotcom_chat.activate', {
      target: structuredItems !== undefined ? 'LIST_BLOCK_PLACEHOLDER_HYDRATED' : 'LIST_BLOCK_PLACEHOLDER_FALLBACK',
      mode: 'immersive',
      type,
    })
  }, [refId, isBlockStreaming, structuredItems, type])

  // Once complete we render the actual list
  return (
    <div>
      <ul id={listId} className={clsx(styles.list, isBlockStreaming && styles.streaming)}>
        {visibleItems.map((item, index) => {
          let newItem = {...item, type}

          if (type === 'task' && item.url) {
            const liveTaskState = lookupTaskState(taskStateMap, item.url)
            if (liveTaskState) {
              newItem = {
                ...newItem,
                state: liveTaskState.state,
                title: liveTaskState.title ?? newItem.title,
              }
            }
          }

          const isStreaming = isBlockStreaming && index === visibleItems.length - 1

          const {icon, colorClass} = getIconProps(newItem)
          return (
            // The items cannot reorder and we want to ensure a stable key, so it's safe to use index
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <li className={styles.item} key={index} ref={index === initialVisibleCount ? firstNewItemRef : undefined}>
              <div className={styles.content}>
                <div className={clsx(colorClass, styles.icon)}>{icon}</div>
                <div className={styles.header}>
                  {
                    // While streaming it's important that we don't render the link to avoid a11y issues and let it fade in later
                    isStreaming && !newItem.title ? null : (
                      <a
                        {...testIdProps('issue-list-item-link')}
                        href={newItem.url ?? '#'}
                        className={styles.link}
                        onClick={e => {
                          if (e.metaKey || e.ctrlKey) return
                          e.preventDefault()
                          const targetUrl = newItem.url
                          if (!targetUrl) return

                          // Only updateItem if it's an issue
                          if (newItem.type === 'issue') {
                            sendEvent('dotcom_chat.activate', {target: 'ISSUE_LIST_BLOCK_ITEM_OPEN', mode: 'immersive'})
                            const {owner, repo, number} = parseGitHubIssueUrl(targetUrl)
                            if (owner && repo && number) {
                              const id = `issue:${owner}/${repo}/${number}` as const
                              updateItem({
                                messageId,
                                name: newItem.title ?? `Issue #${number}`,
                                state:
                                  newItem.state === 'open' || newItem.state === 'closed' ? newItem.state : undefined,
                                number,
                                owner,
                                repo,
                                id,
                                href: targetUrl,
                                type: 'issue',
                              })
                              openItem(id)
                              openPreviewPane(e.currentTarget)
                            }
                          } else if (newItem.type === 'task') {
                            sendEvent('dotcom_chat.activate', {target: 'TASK_LIST_BLOCK_ITEM_OPEN', mode: 'immersive'})
                            window.open(targetUrl, '_self')
                          } else {
                            sendEvent('dotcom_chat.activate', {target: 'PR_LIST_BLOCK_ITEM_OPEN', mode: 'immersive'})
                            // If it's a PR, just open the URL
                            window.open(targetUrl, '_self')
                          }
                        }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {newItem.title ? (
                          <>
                            <span className={styles.title}>{newItem.title}</span>
                            {newItem.number !== undefined && newItem.type !== 'task' && (
                              <span className={styles.number}>#{newItem.number}</span>
                            )}
                          </>
                        ) : newItem.number !== undefined ? (
                          // Fall back to the number as the title
                          <span className={styles.title}>#{newItem.number}</span>
                        ) : (
                          // If the issue has streamed and we never got a title _or_ a number, the AI is throwing a
                          // total hissy fit and there's nothing we can do. Hopefully this doesn't actually happen.
                          <span className={clsx(styles.title, styles.unknownTitle)}>
                            {newItem.type === 'task' ? 'Unknown task' : 'Unknown issue'}
                          </span>
                        )}
                      </a>
                    )
                  }
                  {newItem.type !== 'task' && (
                    <div className={styles.labels}>
                      {newItem.labels?.length
                        ? newItem.labels.map(label => (
                            <Label size="small" variant="secondary" key={label} className={styles.label}>
                              <Truncate inline maxWidth="100%" title={label}>
                                {label}
                              </Truncate>
                            </Label>
                          ))
                        : null}
                      {newItem.comments !== 0 && newItem.comments !== undefined && (
                        <Label size="small" variant="secondary">
                          <CommentIcon className={styles.labelIcon} size={12} />
                          {newItem.comments}
                        </Label>
                      )}
                    </div>
                  )}
                </div>
                {newItem.type === 'task' ? (
                  newItem.repository ? (
                    <div className={styles.description}>{newItem.repository}</div>
                  ) : null
                ) : (
                  <Description item={newItem} isStreaming={isStreaming} />
                )}
                {newItem.type !== 'task' && (
                  <div className={styles.metadata}>
                    {newItem.assignees_avatar_urls === undefined ? null : newItem.assignees_avatar_urls.length === 0 ? (
                      <div className={styles.unassignedAvatar} role="img" aria-label="Unassigned">
                        <PersonIcon size={12} />
                      </div>
                    ) : (
                      <div
                        role="img"
                        // We don't know the assignee names so we can't provide an aria-label to each avatar
                        aria-label={`${newItem.assignees_avatar_urls.length} ${
                          newItem.assignees_avatar_urls.length === 1 ? 'assignee' : 'assignees'
                        }`}
                      >
                        <AvatarStack disableExpand>
                          {newItem.assignees_avatar_urls.map(url => (
                            <GitHubAvatar key={url} src={url} alt="Assignee avatar" />
                          ))}
                        </AvatarStack>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </li>
          )
        })}
        {hasMoreItems && (
          <li className={styles.item}>
            <div className={clsx(styles.content, styles.toggleContent)}>
              <button
                type="button"
                className={clsx(styles.toggleButton, isBlockStreaming && styles.shimmer)}
                onClick={() => {
                  setCollapsed(prev => {
                    if (prev) justExpandedRef.current = true
                    return !prev
                  })
                }}
                aria-expanded={!collapsed}
                aria-controls={listId}
              >
                <span className={styles.toggleIcon}>
                  {collapsed ? <UnfoldIcon size={16} /> : <FoldIcon size={16} />}
                </span>
                {collapsed ? `Show ${items.length - initialVisibleCount} more` : 'Show less'}
              </button>
            </div>
          </li>
        )}
      </ul>
    </div>
  )
}
